/*
 * Free gift auto-add for the cart drawer.
 * Adds the configured gift variant when the qualifying (non-gift) subtotal
 * reaches the threshold, removes it when the cart drops below, and respects a
 * shopper manually removing the gift (will not re-add it in the same session).
 *
 * Config comes from window.freeGiftConfig (set in snippets/cart-drawer.liquid):
 *   { enabled, thresholdCents, giftVariantId, respectRemoval }
 *
 * UPDATED: now also (a) intercepts fetch/XHR calls to /cart/add|change|update|clear
 * from ANY source (theme UI, third-party apps, or external checkout assistants
 * like Microsoft Clarity's AI chat that call the Storefront/Cart API directly
 * and never fire window.eventBus), and (b) intercepts checkout-button clicks
 * to force a final sync before navigation. This closes the gap where a
 * customer could reach checkout without the gift being added because the
 * triggering flow bypassed the theme's own cart drawer events.
 */
(function () {
  "use strict";

  var GIFT_PROP = "_free_gift";
  var busy = false;
  var pending = false;
  var selfOp = false;

  function cfg() {
    return window.freeGiftConfig || null;
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  var _origFetch = window.fetch;

  function getCart() {
    return _origFetch(root() + "cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  }

  function qualifyingSubtotal(cart, giftId) {
    return cart.items.reduce(function (sum, item) {
      if (item.variant_id === giftId) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function findGift(cart, giftId) {
    return cart.items.filter(function (i) {
      return i.variant_id === giftId;
    })[0];
  }

  function refreshDrawer() {
    return _origFetch(root() + "?sections=cart-drawer")
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json && json["cart-drawer"] && window.eventBus) {
          window.eventBus.emit("update:cart:drawer", { sections: { "cart-drawer": json["cart-drawer"] } });
        }
      })
      .catch(function () {});
  }

  function giftFetch(url, body) {
    selfOp = true;
    return _origFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    })
      .catch(function () {})
      .finally(function () {
        selfOp = false;
      });
  }

  function addGift(giftId) {
    var props = {};
    props[GIFT_PROP] = "true";
    return giftFetch(root() + "cart/add.js", { items: [{ id: giftId, quantity: 1, properties: props }] });
  }

  function setGiftQty(key, qty) {
    return giftFetch(root() + "cart/change.js", { id: key, quantity: qty });
  }

  function sync() {
    var c = cfg();
    if (!c || !c.enabled) return Promise.resolve();
    if (busy) {
      pending = true;
      return Promise.resolve();
    }
    busy = true;

    return getCart()
      .then(function (cart) {
        var giftId = c.giftVariantId;
        var qualifying = qualifyingSubtotal(cart, giftId);
        var gift = findGift(cart, giftId);
        var atThreshold = qualifying >= c.thresholdCents;

        if (!atThreshold) {
          sessionStorage.removeItem("freeGiftDismissed");
          sessionStorage.removeItem("freeGiftAdded");
          if (gift) {
            return setGiftQty(gift.key, 0).then(refreshDrawer);
          }
          return null;
        }

        if (gift) {
          if (gift.quantity > 1) {
            return setGiftQty(gift.key, 1).then(refreshDrawer);
          }
          return null;
        }

        var dismissed = sessionStorage.getItem("freeGiftDismissed") === "1";
        if (dismissed) {
          return null;
        }

        if (c.respectRemoval && sessionStorage.getItem("freeGiftAdded") === "1") {
          sessionStorage.setItem("freeGiftDismissed", "1");
          sessionStorage.removeItem("freeGiftAdded");
          return null;
        }

        return addGift(giftId).then(function () {
          sessionStorage.setItem("freeGiftAdded", "1");
          return refreshDrawer();
        });
      })
      .catch(function (e) {
        if (window.console) console.error("free-gift sync error:", e);
      })
      .then(function () {
        busy = false;
        if (pending) {
          pending = false;
          setTimeout(sync, 30);
        }
      });
  }

  function schedule() {
    setTimeout(sync, 80);
  }

  /* ---- NEW: global network interception ----
   * Catches cart mutations made by ANY code path (theme, third-party apps,
   * or external assistants like Microsoft Clarity that call the Cart API
   * directly), not just ones that go through window.eventBus.
   */
  window.fetch = function () {
    var a0 = arguments[0];
    var url = a0 ? (typeof a0 === "string" ? a0 : a0.url || "") : "";
    var isCartOp = url && /\/cart\/(add|change|update|clear)/.test(url);
    var p = _origFetch.apply(this, arguments);
    if (isCartOp && !selfOp && p && typeof p.then === "function") {
      p.then(function () {
        schedule();
      }).catch(function () {});
    }
    return p;
  };

  try {
    var XO = XMLHttpRequest.prototype.open;
    var XS = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) {
      try {
        this.__freeGiftCartOp = !!(u && /\/cart\/(add|change|update|clear)/.test(u));
      } catch (e) {
        this.__freeGiftCartOp = false;
      }
      return XO.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__freeGiftCartOp && !selfOp) {
        this.addEventListener("loadend", function () {
          if (!selfOp) schedule();
        });
      }
      return XS.apply(this, arguments);
    };
  } catch (e) {}

  /* ---- NEW: checkout click interception ----
   * Force a final gift check/add BEFORE navigating to checkout, regardless
   * of which UI element (theme cart drawer, a chat assistant's own button,
   * etc.) triggered the click. This ensures the gift is present even if the
   * customer reaches checkout through a path that never touched the cart
   * drawer at all.
   */
  var passThrough = false;
  document.addEventListener(
    "click",
    function (e) {
      if (passThrough) return;
      var c = cfg();
      if (!c || !c.enabled) return;
      var t = e.target;
      var btn = t && t.closest
        ? t.closest('[name="checkout"], button[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"], a[href$="/cart/checkout"]')
        : null;
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      sync().finally(function () {
        passThrough = true;
        try {
          btn.click();
        } catch (e2) {
          if (btn.href) window.location.href = btn.href;
        }
        setTimeout(function () {
          passThrough = false;
        }, 1500);
      });
    },
    true
  );

  function init() {
    if (window.eventBus) {
      window.eventBus.on("update:cart:drawer", schedule);
      window.eventBus.on("render:cart:drawer", schedule);
      window.eventBus.on("open:cart:drawer", schedule);
    }
    setTimeout(sync, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
